import React, { useEffect, useMemo, useRef, useState } from 'react';
import { timeFormat } from 'd3-time-format';
import { BarChart, BarDatum, BarSeries } from '../bar-chart/bar-chart';
import { TrendLine, TrendPoint } from '../trend-line/trend-line';
import { Tile } from '../tile';
import { chartTokens } from '../charts/chart-tokens';
import { ChartMargin } from '../charts/chart-types';
import { formatCompact } from '../charts/chart-utils';
import './automation-trend-chart.scss';

export type AutomationTrendTab = 'Daily' | 'Weekly';

export interface DateRangeSelection {
  presetId: string;
  dos_start: string;
  dos_end: string;
}

export interface AutomationTrendChartProps {
  data: TrendPoint[];
  series: BarSeries[];
  barData: BarDatum[];
  /** When false, suppresses the inline HS/SL/date filter row. */
  showFilters?: boolean;
  width?: number;
  height?: number;
  xTickFormat?: (date: string) => string;
  ariaLabel?: string;
  // Filter props still accepted (passed through from dashboard for story/test compat)
  healthSystem?: string;
  healthSystemOptions?: { value: string; label: string }[];
  onHealthSystemChange?: (code: string) => void;
  serviceLine?: string;
  serviceLineOptions?: { value: string; label: string }[];
  onServiceLineChange?: (code: string) => void;
  dateRangePresetId?: string;
  dateRange?: { dos_start: string; dos_end: string };
  dateRangePillLabel?: string;
  dateRangePresets?: unknown[];
  onDateRangeChange?: (selection: DateRangeSelection) => void;
  onExpand?: () => void;
  onSettings?: () => void;
}

const DEFAULT_MARGIN: ChartMargin = { top: 10, right: 50, bottom: 28, left: 44 };

const formatShortDate = timeFormat('%-m/%-d');

const defaultTickFormat = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? date : formatShortDate(parsed);
};

// ── ISO-week bucketing for Weekly view ───────────────────────────────────────
const isoWeekKey = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  const day = d.getDay(); // 0 = Sun
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((day + 6) % 7));
  const yyyy = monday.getFullYear();
  const mm = String(monday.getMonth() + 1).padStart(2, '0');
  const dd = String(monday.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const bucketWeekly = (
  data: TrendPoint[],
  barData: BarDatum[],
): { data: TrendPoint[]; barData: BarDatum[] } => {
  const trendMap = new Map<string, { auto: number; manual: number; total: number }>();
  barData.forEach((bd, i) => {
    const key = isoWeekKey(String(data[i]?.date ?? bd.category));
    const prev = trendMap.get(key) ?? { auto: 0, manual: 0, total: 0 };
    trendMap.set(key, {
      auto: prev.auto + (bd.values['automated'] ?? 0),
      manual: prev.manual + (bd.values['manual'] ?? 0),
      total: prev.total + ((bd.values['automated'] ?? 0) + (bd.values['manual'] ?? 0)),
    });
  });

  const weeklyData: TrendPoint[] = [];
  const weeklyBarData: BarDatum[] = [];
  trendMap.forEach((vals, key) => {
    const total = vals.auto + vals.manual;
    weeklyData.push({
      date: key,
      value: total,
      rate: total > 0 ? (vals.auto / total) * 100 : 0,
    });
    weeklyBarData.push({
      category: key,
      values: { automated: vals.auto, manual: vals.manual },
    });
  });

  return { data: weeklyData, barData: weeklyBarData };
};

// ── Tooltip ──────────────────────────────────────────────────────────────────
interface TooltipInfo {
  /** SVG-space x for the indicator line and dot. */
  svgX: number;
  /** SVG-space y for the rate-line dot. */
  svgRateY: number;
  /** Viewport x for the fixed-position tooltip. */
  fixedX: number;
  /** Viewport y for the fixed-position tooltip. */
  fixedY: number;
  date: string;
  automated: number;
  manual: number;
  total: number;
  rate: number | undefined;
}

interface ChartTooltipProps {
  info: TooltipInfo;
}

const ChartTooltip: React.FC<ChartTooltipProps> = ({ info }) => (
  <div
    className="trend-tooltip"
    style={{ left: info.fixedX, top: info.fixedY }}
    role="tooltip"
    aria-live="polite">
    <div className="trend-tooltip__date">{info.date}</div>
    <div className="trend-tooltip__row">
      <span className="trend-tooltip__swatch" style={{ background: chartTokens.series.automated }} />
      <span>Automated</span>
      <strong>{formatCompact(info.automated)}</strong>
    </div>
    <div className="trend-tooltip__row">
      <span className="trend-tooltip__swatch" style={{ background: chartTokens.series.manual }} />
      <span>Manual</span>
      <strong>{formatCompact(info.manual)}</strong>
    </div>
    {info.rate != null && (
      <div className="trend-tooltip__rate">
        Rate: <strong style={{ color: '#F17053' }}>{info.rate.toFixed(1)}%</strong>
      </div>
    )}
    <div className="trend-tooltip__caret" aria-hidden="true" />
  </div>
);

// ── Segmented tab ─────────────────────────────────────────────────────────────
interface TrendTabsProps {
  active: AutomationTrendTab;
  onChange: (tab: AutomationTrendTab) => void;
}

const TrendTabs: React.FC<TrendTabsProps> = ({ active, onChange }) => (
  <div className="trend-tabs" role="tablist" aria-label="Trend granularity">
    {(['Daily', 'Weekly'] as AutomationTrendTab[]).map((tab) => (
      <button
        key={tab}
        role="tab"
        aria-selected={active === tab}
        className={
          active === tab ? 'trend-tabs__tab trend-tabs__tab--active' : 'trend-tabs__tab'
        }
        onClick={() => onChange(tab)}
        type="button">
        {tab}
      </button>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
export const AutomationTrendChart: React.FC<AutomationTrendChartProps> = ({
  data,
  series,
  barData,
  showFilters = true,
  width,
  height = 320,
  xTickFormat = defaultTickFormat,
  ariaLabel = 'Automation trend',
}) => {
  const [trendView, setTrendView] = useState<AutomationTrendTab>('Daily');
  const [tooltip, setTooltip] = useState<TooltipInfo | null>(null);
  const plotRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number>(width ?? 720);
  const [measuredHeight, setMeasuredHeight] = useState<number>(height);

  useEffect(() => {
    const node = plotRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      if (width == null) {
        const nextW = Math.round(entry.contentRect.width);
        if (nextW > 0) setMeasuredWidth(nextW);
      }
      const nextH = Math.round(entry.contentRect.height);
      if (nextH > 0) setMeasuredHeight(nextH);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [width]);

  const chartWidth = width ?? measuredWidth;
  const chartHeight = measuredHeight;

  // Apply Weekly bucketing client-side when tab is Weekly
  const { data: activeData, barData: activeBarData } = useMemo(() => {
    if (trendView === 'Weekly') return bucketWeekly(data, barData);
    return { data, barData };
  }, [trendView, data, barData]);

  const trendLineData = useMemo<TrendPoint[]>(
    () => activeData.map((d) => ({ date: String(d.date), value: d.value, rate: d.rate })),
    [activeData],
  );

  const rateMax = useMemo(() => {
    const present = activeData.some((d) => d.rate != null);
    return present ? 100 : 0;
  }, [activeData]);

  // Show ~10 x-axis labels regardless of period length
  const thinXTickFormat = useMemo(() => {
    const step = Math.max(1, Math.round(activeBarData.length / 10));
    return (v: string | number): string => {
      const idx = activeBarData.findIndex((b) => b.category === String(v));
      return idx % step === 0 ? xTickFormat(String(v)) : '';
    };
  }, [activeBarData, xTickFormat]);

  const handleBarHover = (datum: BarDatum, _seriesKey: string) => {
    const automated = datum.values['automated'] ?? 0;
    const manual = datum.values['manual'] ?? 0;
    const total = automated + manual;
    const point = activeData.find((d) => String(d.date) === datum.category);

    const idx = activeBarData.findIndex((b) => b.category === datum.category);
    const innerW = chartWidth - DEFAULT_MARGIN.left - DEFAULT_MARGIN.right;
    const barSlotW = activeBarData.length > 0 ? innerW / activeBarData.length : 0;
    const svgX = DEFAULT_MARGIN.left + idx * barSlotW + barSlotW / 2;

    // Rate dot y in SVG space (secondary scale: 0-100 mapped to innerHeight→0)
    const innerH = chartHeight - DEFAULT_MARGIN.top - DEFAULT_MARGIN.bottom;
    const rate = point?.rate;
    const svgRateY =
      rate != null
        ? DEFAULT_MARGIN.top + innerH * (1 - rate / 100)
        : DEFAULT_MARGIN.top + innerH / 2;

    // Convert to viewport coords for position: fixed tooltip
    const rect = plotRef.current?.getBoundingClientRect();
    const fixedX = (rect?.left ?? 0) + svgX;
    const fixedY = (rect?.top ?? 0) + svgRateY;

    setTooltip({
      svgX,
      svgRateY,
      fixedX,
      fixedY,
      date: xTickFormat(datum.category),
      automated,
      manual,
      total,
      rate,
    });
  };

  const handleMouseLeave = () => setTooltip(null);

  // Inline legend for Tile actions slot
  const legendAndTabs = (
    <div className="automation-trend-chart__header-right">
      <div className="automation-trend-chart__legend" aria-hidden="true">
        {series.map((s) => (
          <div key={s.key} className="automation-trend-chart__legend-item">
            <span
              className="automation-trend-chart__legend-swatch"
              style={{ background: s.color ?? chartTokens.series.automated }}
            />
            <span className="automation-trend-chart__legend-label">{s.label}</span>
          </div>
        ))}
        <div className="automation-trend-chart__legend-item">
          <span
            className="automation-trend-chart__legend-dash"
            style={{ background: chartTokens.rateLine }}
          />
          <span className="automation-trend-chart__legend-label">Rate %</span>
        </div>
      </div>
      <TrendTabs active={trendView} onChange={setTrendView} />
    </div>
  );

  return (
    <Tile
      title="Automation Trend"
      subtitle="Daily volume and automation rate"
      actions={legendAndTabs}
      className="automation-trend-chart"
      ariaLabel={ariaLabel}>
      <div
        ref={plotRef}
        className="automation-trend-chart__plot"
        onMouseLeave={handleMouseLeave}>
        <div className="automation-trend-chart__layer">
          <BarChart
            data={activeBarData}
            series={series}
            variant="stacked"
            width={chartWidth}
            height={chartHeight}
            margin={DEFAULT_MARGIN}
            xAxis={{ tickFormat: thinXTickFormat }}
            ariaLabel={`${ariaLabel} bars`}
            onBarHover={handleBarHover}
          />
        </div>
        {activeData.some((d) => d.rate != null) ? (
          <div className="automation-trend-chart__layer automation-trend-chart__layer--overlay">
            <TrendLine
              data={trendLineData}
              width={chartWidth}
              height={chartHeight}
              margin={DEFAULT_MARGIN}
              xScaleType="band"
              showPrimary={false}
              primaryAxis={{ show: false }}
              secondaryAxis={{ domain: [0, rateMax || 100], show: true }}
              secondaryColor={chartTokens.rateLine}
              strokeDasharray="none"
              rateDots
              ariaLabel={`${ariaLabel} rate`}
            />
          </div>
        ) : null}
        {tooltip && (
          <div className="automation-trend-chart__layer automation-trend-chart__layer--overlay">
            <svg
              width={chartWidth}
              height={chartHeight}
              aria-hidden="true"
              style={{ overflow: 'visible' }}>
              <line
                x1={tooltip.svgX}
                y1={DEFAULT_MARGIN.top}
                x2={tooltip.svgX}
                y2={chartHeight - DEFAULT_MARGIN.bottom}
                stroke="rgba(35,31,32,0.28)"
                strokeWidth={1}
                strokeDasharray="4 3"
              />
              {tooltip.rate != null && (
                <circle
                  cx={tooltip.svgX}
                  cy={tooltip.svgRateY}
                  r={5}
                  fill={chartTokens.rateLine}
                  stroke="#fff"
                  strokeWidth={2}
                />
              )}
            </svg>
          </div>
        )}
        {tooltip && <ChartTooltip info={tooltip} />}
      </div>
    </Tile>
  );
};

AutomationTrendChart.displayName = 'AutomationTrendChart';
